import { useState, useRef } from "react";
import { Link, useNavigate } from "react-router-dom";
import { motion, AnimatePresence } from "framer-motion";
import { ArrowRight, ArrowLeft, CheckCircle, House, SealCheck, ArrowUpRight, CalendarPlus } from "@phosphor-icons/react";
import api, { formatApiErrorDetail } from "@/lib/apiClient";
import { BRAND, LENDER } from "@/lib/constants";
import { toast } from "sonner";
import { track } from "@/lib/tracking";

function Field({ field, value, onChange }) {
  const id = `field-${field.name}`;

  if (field.type === "choice") {
    const multi = field.multi;
    const selected = multi ? (Array.isArray(value) ? value : []) : value;
    const isActive = (opt) => (multi ? selected.includes(opt) : selected === opt);
    const toggle = (opt) => {
      if (multi) {
        const next = selected.includes(opt) ? selected.filter((x) => x !== opt) : [...selected, opt];
        onChange(field.name, next);
      } else {
        onChange(field.name, opt);
      }
    };
    return (
      <div>
        <label className="lux-label">
          {field.label}
          {multi && <span className="ml-2 text-white/30 normal-case tracking-normal">(select all that apply)</span>}
        </label>
        <div className="grid grid-cols-2 gap-3 sm:grid-cols-3">
          {field.options.map((opt) => (
            <button
              type="button"
              key={opt}
              data-testid={`choice-${field.name}-${opt.toLowerCase().replace(/[^a-z0-9]+/g, "-").replace(/^-+|-+$/g, "")}`}
              aria-pressed={isActive(opt)}
              data-selected={isActive(opt) ? "true" : "false"}
              onClick={() => toggle(opt)}
              className={`px-4 py-3 text-sm rounded-sm border transition-all duration-200 text-left ${
                isActive(opt)
                  ? "border-brand bg-brand/10 text-brand"
                  : "border-white/15 text-white/70 hover:border-white/40 hover:text-white"
              }`}
            >
              {opt}
            </button>
          ))}
        </div>
      </div>
    );
  }

  if (field.type === "textarea") {
    return (
      <div>
        <label className="lux-label" htmlFor={id}>{field.label}</label>
        <textarea
          id={id}
          data-testid={`input-${field.name}`}
          rows={3}
          className="lux-input resize-none"
          placeholder={field.placeholder}
          value={value || ""}
          onChange={(e) => onChange(field.name, e.target.value)}
        />
      </div>
    );
  }

  return (
    <div>
      <label className="lux-label" htmlFor={id}>{field.label}</label>
      <input
        id={id}
        data-testid={`input-${field.name}`}
        type={field.type || "text"}
        className="lux-input"
        placeholder={field.placeholder}
        value={value || ""}
        onChange={(e) => onChange(field.name, e.target.value)}
      />
    </div>
  );
}

export default function FunnelForm({ leadType, title, subtitle, accent, steps, bgImage, matchOnSuccess }) {
  const [step, setStep] = useState(0);
  const [data, setData] = useState({});
  const [submitting, setSubmitting] = useState(false);
  const [done, setDone] = useState(false);
  const partialSent = useRef(false);
  const navigate = useNavigate();

  const total = steps.length;
  const current = steps[step];

  const onChange = (name, value) => setData((d) => ({ ...d, [name]: value }));

  const isEmpty = (f) => {
    const v = data[f.name];
    if (f.type === "choice" && f.multi) return !Array.isArray(v) || v.length === 0;
    return !v;
  };

  const validateStep = () => {
    for (const f of current.fields) {
      if (f.required && isEmpty(f)) {
        toast.error(`Please provide: ${f.label}`);
        return false;
      }
      if (f.type === "email" && data[f.name] && !/^\S+@\S+\.\S+$/.test(data[f.name])) {
        toast.error("Please enter a valid email address");
        return false;
      }
    }
    return true;
  };

  const next = async () => {
    if (!validateStep()) return;
    if (step < total - 1) {
      // Partial capture: once we have name + email, an abandon later still
      // leaves a lead (final submit dedupe-merges into the same record).
      if (!partialSent.current && data.name && data.email) {
        partialSent.current = true;
        api.post("/leads", {
          lead_type: leadType,
          ...data,
          message: `${data.message || ""} [partial — still completing the ${leadType} funnel]`.trim(),
        }).catch(() => { partialSent.current = false; });
      }
      setStep((s) => s + 1);
      return;
    }
    setSubmitting(true);
    try {
      await api.post("/leads", { lead_type: leadType, ...data });
      track("lead", { type: leadType });
      setDone(true);
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail) || e.message);
    } finally {
      setSubmitting(false);
    }
  };

  const goToMatches = () => {
    const params = new URLSearchParams();
    if (Array.isArray(data.areas) && data.areas.length) params.set("region", data.areas.join(","));
    if (Array.isArray(data.property_type) && data.property_type.length) params.set("property_type", data.property_type.join(","));
    navigate(`/listings?${params.toString()}`);
  };

  return (
    <div className="relative min-h-screen bg-background flex items-center justify-center px-4 py-16 overflow-hidden">
      <div className="absolute inset-0 bg-cover bg-center" style={{ backgroundImage: `url(${bgImage})` }} />
      <div className="absolute inset-0 bg-background/80" />
      <div className="absolute inset-0 bg-gradient-to-t from-background via-background/60 to-background/30" />

      <div className="absolute top-6 left-6 right-6 z-20 flex items-center justify-between gap-3">
        <Link
          to="/"
          data-testid="funnel-home-link"
          className="flex items-center gap-2 text-white/70 hover:text-brand transition-colors text-sm tracking-wide"
        >
          <House size={18} weight="duotone" /> {BRAND.mark}
        </Link>
        <Link
          to="/"
          data-testid="funnel-home-btn"
          className="inline-flex items-center gap-2 px-4 py-2 rounded-sm border border-white/20 text-xs uppercase tracking-[0.15em] text-white/80 hover:border-brand hover:text-brand transition-colors"
        >
          <House size={14} weight="duotone" /> Home
        </Link>
      </div>

      <motion.div
        initial={{ opacity: 0, y: 30 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.7, ease: [0.16, 1, 0.3, 1] }}
        className="relative z-10 w-full max-w-xl backdrop-blur-xl bg-[#08090C]/60 border border-white/10 shadow-2xl rounded-sm p-8 sm:p-12"
      >
        {done ? (
          <div className="text-center py-8" data-testid="funnel-success">
            <CheckCircle size={64} weight="duotone" className="mx-auto text-brand mb-6" />
            <h2 className="text-3xl text-white mb-3">You're all set.</h2>
            <p className="text-white/60 font-light leading-relaxed">
              Thank you. {BRAND.agentFirst} has received your details and will personally reach out
              shortly with next steps. Keep an eye on your inbox and phone.
            </p>

            {leadType === "buyer" && (
              <Link
                to="/financing"
                data-testid="funnel-get-preapproved"
                className="group mt-7 flex items-center justify-between gap-4 rounded-sm border border-brand/30 bg-brand/[0.06] px-5 py-4 text-left transition-colors hover:bg-brand/[0.12]"
              >
                <div className="flex items-center gap-3">
                  <SealCheck size={30} weight="duotone" className="shrink-0 text-brand" />
                  <div>
                    <p className="text-sm font-medium text-white">Need financing? Meet Jeff</p>
                    <p className="text-xs text-white/50">Sharpens your offer · {LENDER.name}, {LENDER.titleLong}</p>
                  </div>
                </div>
                <span className="flex items-center gap-1.5 whitespace-nowrap rounded-sm bg-brand px-4 py-2 text-xs font-medium uppercase tracking-wider text-black transition-transform group-hover:translate-x-0.5">
                  Learn more <ArrowUpRight size={14} weight="bold" />
                </span>
              </Link>
            )}

            <div className="mt-8 flex flex-col sm:flex-row gap-3 justify-center">
              {matchOnSuccess && (
                <button
                  onClick={goToMatches}
                  data-testid="funnel-view-matches"
                  className="px-8 py-4 rounded-sm bg-brand text-black text-sm uppercase tracking-[0.1em] font-medium hover:bg-brand-hover transition-colors"
                >
                  View matching homes
                </button>
              )}
              <Link
                to="/book"
                data-testid="funnel-book-call"
                className={`px-8 py-4 rounded-sm text-sm uppercase tracking-[0.1em] font-medium transition-colors inline-flex items-center justify-center gap-2 ${
                  matchOnSuccess
                    ? "border border-brand/40 text-brand hover:bg-brand/10"
                    : "bg-brand text-black hover:bg-brand-hover"
                }`}
              >
                <CalendarPlus size={16} weight="bold" /> Book a free call
              </Link>
              <Link
                to="/"
                data-testid="funnel-back-home"
                className="px-8 py-4 rounded-sm border border-white/20 text-white text-sm uppercase tracking-[0.1em] font-medium hover:border-white/50 transition-colors"
              >
                Back to home
              </Link>
            </div>
          </div>
        ) : (
          <>
            <div className="mb-8">
              <p className="text-xs uppercase tracking-[0.25em] mb-3" style={{ color: accent }}>{subtitle}</p>
              <h1 className="text-3xl sm:text-4xl text-white leading-tight">{title}</h1>
            </div>

            <div className="mb-8">
              <div className="flex justify-between text-xs text-white/40 mb-2">
                <span>Step {step + 1} of {total}</span>
                <span>{Math.round(((step + 1) / total) * 100)}%</span>
              </div>
              <div className="h-1 w-full bg-white/10 rounded-full overflow-hidden">
                <motion.div className="h-full bg-brand" animate={{ width: `${((step + 1) / total) * 100}%` }} transition={{ duration: 0.4 }} />
              </div>
            </div>

            <AnimatePresence mode="wait">
              <motion.div
                key={step}
                initial={{ opacity: 0, x: 24 }}
                animate={{ opacity: 1, x: 0 }}
                exit={{ opacity: 0, x: -24 }}
                transition={{ duration: 0.35 }}
                className="space-y-6"
              >
                <h3 className="text-lg text-white/90">{current.title}</h3>
                {current.fields.map((f) => (
                  <Field key={f.name} field={f} value={data[f.name]} onChange={onChange} />
                ))}
              </motion.div>
            </AnimatePresence>

            <div className="flex items-center justify-between mt-10">
              {step > 0 ? (
                <button
                  type="button"
                  data-testid="funnel-back-btn"
                  onClick={() => setStep((s) => s - 1)}
                  className="flex items-center gap-2 text-white/60 hover:text-white text-sm transition-colors"
                >
                  <ArrowLeft size={16} /> Back
                </button>
              ) : (<span />)}
              <button
                type="button"
                data-testid="funnel-next-btn"
                onClick={next}
                disabled={submitting}
                className="flex items-center gap-2 px-8 py-4 rounded-sm bg-brand text-black text-sm uppercase tracking-[0.1em] font-medium hover:bg-brand-hover transition-colors disabled:opacity-60"
              >
                {submitting ? "Sending..." : step < total - 1 ? "Continue" : "Get My Results"}
                {!submitting && <ArrowRight size={16} weight="bold" />}
              </button>
            </div>
          </>
        )}
      </motion.div>
    </div>
  );
}
